/***************************************************************************
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/

import { ModelInputAnimationStatus, RendererOptions } from '@a3d-viewer/viewer'
import { atom } from 'jotai'

export const DEMO_THEME_KEY = 'demo-theme'

// pre react to avoid blinking
if (localStorage[DEMO_THEME_KEY] === 'dark') {
  document.documentElement.classList.add('dark')
}

export enum Theme {
  light = 'light',
  dark = 'dark',
}

export let defaultAppTheme = Theme.dark

export const themeAtom = atom<Theme>(
  (localStorage[DEMO_THEME_KEY] as Theme) || defaultAppTheme
)

export enum EnvSrc {
  studioHigh = 'https://cdn.substance3d.com/v2/files/public/studio.hdr',
  studioWhiteHdr = 'https://cdn.substance3d.com/v2/files/public/studio-white.hdr',
  studioWhiteEnv = 'https://cdn.substance3d.com/v2/files/public/studio-white.env',
  bridgeEnv = 'https://assets.babylonjs.com/environments/sanGiuseppeBridge.env',
  plazaEnv = 'https://assets.babylonjs.com/environments/ulmerMuenster.env',
  roomHdr = 'https://playground.babylonjs.com/textures/room.hdr',
  workshopEnv = 'https://cdn.substance3d.com/v2/files/public/Atelier1K.env',
  workshopHdr = 'https://cdn.substance3d.com/v2/files/public/Atelier1K.hdr',
  exteriorEnv = 'https://cdn.substance3d.com/v2/files/public/Exterior1K.env',
  exteriorHdr = 'https://cdn.substance3d.com/v2/files/public/Exterior1K.hdr',
}

export interface InputVector {
  x: number
  y: number
  z: number
}

export type A3dHotSpotSurfaceProps = {
  id?: string
  modelIndex: number
  meshIndex: number
  pointIndex: [number, number, number]
  barycentric: [number, number, number]
  cameraOrbit?: [number, number, number]
}

export type A3dHotSpotWorldProps = {
  id?: string
  position: [number, number, number]
  normal: [number, number, number]
  cameraOrbit?: [number, number, number]
}

export const DEFAULT_ADDITIONAL_MODEL = {
  src: null,
  filename: null,
  layer: 'viewer',
  loader: 'glb',
  translation: { x: 0, y: 0, z: 0 },
  rotation: { x: 0, y: 0, z: 0 },
  scaling: { x: 1, y: 1, z: 1 },
}

export interface ViewerConfig {
  // url of the environment map
  envSrc: EnvSrc
  // rotation in radians
  envRotation: number
  // intensity of the environment map
  envIntensity: number
  // visibility of the environment map
  envVisibility: boolean
  // blur level of the environment map
  envBlurLevel: number
  // color in hex format
  color?: string
  animationAutoPlay: boolean
  // node translation and rotation
  node: {
    translation: InputVector
    rotation: InputVector
    scaling: InputVector
  }
  // model node translation and rotation
  model: {
    translation: InputVector
    rotation: InputVector
    scaling: InputVector
    animations: readonly string[]
    selectedAnimation?: string
    animationStatus?: ModelInputAnimationStatus
  }
  camera: {
    selectedCamera?: string
    orbit: { alpha: number; beta: number; radius: number }
    target: { x: number; y: number; z: number }
    orbitOn: boolean
    targetOn: boolean
  }
  hotSpot: {
    addHotSpotOnNextClick: boolean
    addHotSpotWithCameraOrbit: boolean
    a3dHotSpotsProps: Array<A3dHotSpotSurfaceProps | A3dHotSpotWorldProps>
  }
  additionalModels: {
    src: string | null
    filename: string | null
    layer: string
    loader: string
    translation: InputVector
    rotation: InputVector
    scaling: InputVector
  }[]

  shadow?: boolean
  grid: boolean
  // indicates if the mesh is imported
  modelImported: boolean
  sceneReady: boolean
  toolbar: {
    enable: boolean
  }
  inspector: {
    enable: boolean
  }
  info: {
    enable: boolean
  }
  passes: {
    depth: boolean
    antialiasing: boolean
  }
  fullscreen?: boolean
  cameraReframe: boolean
  cameraLimit: boolean
  rendererOptions: RendererOptions
}

export const DefaultViewerConfig: ViewerConfig = {
  envSrc: EnvSrc.studioWhiteEnv,
  envRotation: 0,
  envIntensity: 1,
  envVisibility: false,
  envBlurLevel: 0,
  color: undefined,
  animationAutoPlay: false,
  node: {
    translation: { x: 0, y: 0, z: 0 },
    rotation: { x: 0, y: 0, z: 0 },
    scaling: { x: 1, y: 1, z: 1 },
  },
  model: {
    translation: { x: 0, y: 0, z: 0 },
    rotation: { x: 0, y: 0, z: 0 },
    scaling: { x: 1, y: 1, z: 1 },
    animations: [],
  },
  camera: {
    orbit: { alpha: 1.57, beta: 0, radius: 1 },
    target: { x: 0.5, y: 0, z: 0 },
    orbitOn: false,
    targetOn: false,
  },
  hotSpot: {
    addHotSpotOnNextClick: false,
    addHotSpotWithCameraOrbit: false,
    a3dHotSpotsProps: [
      {
        modelIndex: 0,
        meshIndex: 1,
        pointIndex: [90, 89, 76],
        barycentric: [0.391, 0.387, 0.223],
        cameraOrbit: [1.27, 1.424, 0.514],
      },
      {
        id: 'MyWorldHotSpot',
        position: [0.074, 0.475, 0.022],
        normal: [0.69, 0.443, -0.572],
        cameraOrbit: [-32.108, 1.112, 1.714],
      },
    ],
  },
  additionalModels: [DEFAULT_ADDITIONAL_MODEL, DEFAULT_ADDITIONAL_MODEL],
  shadow: false,
  grid: false,
  modelImported: false,
  sceneReady: false,
  toolbar: {
    enable: true,
  },
  inspector: {
    enable: false,
  },
  info: {
    enable: false,
  },
  passes: {
    depth: false,
    antialiasing: false,
  },
  fullscreen: false,
  cameraReframe: true,
  cameraLimit: true,
  rendererOptions: {
    aoStrength: {
      type: 'slider',
      value: 0.2,
      min: 0,
      max: 1,
    },
  },
}

const viewerAtom = atom<ViewerConfig>(DefaultViewerConfig)

export { viewerAtom }
